import React, { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { Circles } from 'react-loading-icons';
import { SERVER_URL } from '../../util/EnvironmentConfig';
import axios from 'axios';
import Statistics from './Statistics';
import './custom.css';

const Profile = () => {
  const token = localStorage.getItem('loginToken');
  const [userData, setUserData] = useState(null);
  const server_url = process.env.REACT_APP_API_URI;
  const [userPoints, setUserPoints] = useState(null);
  const [pointHistory, setPointHistory] = useState([]);
  const [badgeProgress, setBadgeProgress] = useState([]);
  const [review, setReview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('profile'); // 'profile' or 'statistics'

  const [isEditing, setIsEditing] = useState(false);
  const [email, setEmail] = useState(userData?.email || '');

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserData(decoded);
      } catch (error) {
        console.error('Invalid token:', error);
        setUserData(null);
      }
    } else {
      setUserData(null);
    }
  }, [token]);

  useEffect(() => {
    if (!userData?.id) return;

    const fetchUserPoints = async () => {
      try {
        const response = await axios.post(
          `${server_url}/user/points-balance`,
          { userId: userData.id },
          { headers: { 'Content-Type': 'application/json' } },
        );
        setUserPoints(response.data.points);
      } catch (error) {
        console.error('Error fetching user points:', error);
      }
    };

    const fetchPointHistory = async () => {
      try {
        const response = await axios.post(
          `${server_url}/user/points-balance-history`,
          { userId: userData.id },
          { headers: { 'Content-Type': 'application/json' } },
        );
        setPointHistory(response.data.pointsHistory || []);
      } catch (error) {
        console.error('Error fetching point history:', error);
      }
    };

    const fetchBadges = async () => {
      try {
        const response = await axios.post(
          `${SERVER_URL}/user/badges`,
          { userId: userData.id },
          { headers: { 'Content-Type': 'application/json' } },
        );
        setBadgeProgress(response.data || []);
      } catch (error) {
        console.error('Error fetching badges:', error);
      }
    };

    const fetchReviews = async () => {
      try {
        const response = await axios.post(
          `${server_url}/user/reviews`,
          { userId: userData.id },
          { headers: { 'Content-Type': 'application/json' } },
        );
        setReview(response.data);

        // setPointHistory(response.data.pointsHistory || []);
      } catch (error) {
        console.error('Error fetching point history:', error);
      }
    };

    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchUserPoints(),
        fetchPointHistory(),
        fetchBadges(),
        fetchReviews(),
      ]);
      setLoading(false);
    };

    fetchData();
  }, [userData, server_url]);

  useEffect(() => {
    if (userData?.email) {
      setEmail(userData.email);
    }
  }, [userData]);

  const getInitials = (name) => {
    return name
      ? name
          .split(' ')
          .map((n) => n[0])
          .join('')
          .toUpperCase()
      : 'A';
  };

  const getRoleName = (role) => {
    switch (role) {
      case 1:
        return 'Admin';
      case 2:
        return 'Student';
      case 3:
        return 'Faculty';
      case 4:
        return 'User';
      default:
        return 'Guest';
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleEmailSave = async () => {
    try {
      await axios.put(`${server_url}/user/user-edit`, {
        id: userData.id,
        email: email,
      });
      console.log('email change');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating email:', error);
    }
  };

  if (!userData) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900 text-white">
        <p className="text-xl font-semibold">No User Data Available</p>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-white">
        <Circles />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-300px)] max-w-screen-xl mx-auto mt-6 mb-4">
      <h1 className="text-4xl font-bold text-secondary text-center mb-4">
        Profile
      </h1>

      {/* View Switcher */}
      <div className="flex justify-center gap-4 mb-6">
        <button
          onClick={() => setActiveView('profile')}
          className={`px-6 py-2 rounded-lg font-semibold transition ${
            activeView === 'profile'
              ? 'bg-green-500 text-white shadow-lg'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Profile
        </button>
        <button
          onClick={() => setActiveView('statistics')}
          className={`px-6 py-2 rounded-lg font-semibold transition ${
            activeView === 'statistics'
              ? 'bg-green-500 text-white shadow-lg'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Statistics
        </button>
      </div>

      {/* Conditional Rendering */}
      {activeView === 'statistics' ? (
        <Statistics userId={userData?.id} />
      ) : (
        <div className=" mx-6 lg:mx-20">
          <div className="bg-white/10 shadow-2xl rounded-2xl p-8  ">
            <div className="flex flex-col items-center text-center">
              <div className="w-24 h-24 flex items-center justify-center bg-gradient-to-r from-green-500 to-emerald-600 text-white text-4xl font-bold rounded-full shadow-lg">
                {getInitials(userData?.name || userData?.fullName || 'User')}
              </div>

              <div className="mt-2">
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      className="bg-gray-700 text-white px-2 py-1 rounded"
                    />
                    <button
                      onClick={handleEmailSave}
                      className="bg-green-500 px-2 py-1 rounded text-white"
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-gray-400 text-lg cursor-pointer hover:text-gray-200">
                      {email || 'ble'}
                    </p>
                    <button
                      className="px-3 py-1 bg-gray-900 text-white rounded  transition"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-3 px-4 py-1 bg-gray-900 text-lg text-white font-extrabold rounded-full shadow">
                {getRoleName(userData?.role)}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
              <div className="bg-gray-900 p-4 rounded-2xl text-center shadow-md mx-4">
                <h3 className="text-lg text-white font-semibold">Points</h3>
                <p className="text-2xl font-bold text-green-400">
                  {userPoints ?? '--'}
                </p>
              </div>

              <div className="bg-gray-900 p-4 rounded-2xl text-center shadow-md mx-4">
                <h3 className="text-lg text-white font-semibold">
                  Badges Progress
                </h3>
                {badgeProgress.map((entry, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-center "
                  >
                    <p className="text-2xl font-bold text-yellow-400">
                      {Math.round(entry.progress)}%
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full ">
              <div className="bg-gray-900 p-4 rounded-2xl text-center shadow-md mx-4">
                <h3 className="text-lg font-semibold text-white">
                  Next Badges
                </h3>
                {badgeProgress.map((entry, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-center gap-2 mt-2"
                  >
                    <span className="text-2xl text-white font-extrabold">
                      {entry.nextBadge || 'No Data'}
                    </span>
                  </div>
                ))}
              </div>

              <div className="bg-gray-900 p-4 rounded-2xl text-center shadow-md mx-4">
                <h3 className="text-lg font-semibold text-white">
                  Badges Earned{' '}
                </h3>
                {badgeProgress.map((entry, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-center gap-2 mt-2"
                  >
                    <span className=" text-2xl text-white font-extrabold">
                      {entry.tier}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* point history */}
            <div className="mt-8 w-full">
              <h4 className="text-xl font-bold text-secondary text-center mb-4 border-b border-gray-600 pb-2">
                Point History
              </h4>
              {pointHistory.length > 0 ? (
                <ul className="custom-scrollbar space-y-3 max-h-56 overflow-y-auto pr-2">
                  {pointHistory.map((entry, index) => (
                    <li
                      key={index}
                      className="bg-gray-900 p-3 rounded-lg flex justify-between items-center shadow-sm"
                    >
                      <span className="text-gray-300 text-sm">
                        {entry.activity || 'N/A'}
                      </span>
                      <span
                        className={`font-semibold ${entry.points > 0 ? 'text-[#5be14b] font-bold' : 'text-red-400'}`}
                      >
                        {entry.points > 0 ? `+${entry.points}` : entry.points}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-400 text-center">
                  No point history available.
                </p>
              )}
            </div>

            {/* latest review */}
            <div className="mt-8 w-full">
              <h4 className="text-xl font-bold text-secondary text-center mb-4 border-b border-gray-600 pb-2">
                Latest Review
              </h4>
              {review.length > 0 ? (
                <ul className="custom-scrollbar space-y-3 max-h-56 overflow-y-auto pr-2">
                  {review.map((entry, index) => (
                    <li
                      key={index}
                      className="bg-gray-900 p-3 rounded-lg flex justify-between items-center shadow-sm text-sm"
                    >
                      <div className="flex flex-col text-gray-300">
                        <span className="font-semibold">
                          {entry.category || 'N/A'}
                        </span>
                        <span className="text-gray-400">
                          {entry.contentName || 'N/A'}
                        </span>
                        <span className="text-gray-500 italic">
                          {entry.family || 'N/A'}
                        </span>
                      </div>
                      <span
                        className={`text-base ${entry.pointsEarned > 0 ? 'text-[#5be14b] font-bold' : 'text-red-400'}`}
                      >
                        {entry.pointsEarned > 0
                          ? `+${entry.pointsEarned}`
                          : entry.pointsEarned}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-400 text-center">
                  No point history available.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
